import { useMemo, useState } from 'react';
import { KeyboardAvoidingView, View } from 'react-native';
import { useSelector } from 'react-redux';
import { Spinner, useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashGet from 'lodash/get';
import lodashIsEmpty from 'lodash/isEmpty';

import EmptyData from '../../Components/EmptyData';
import PageHeader from '../../Components/Header';
import List from '../../Components/List';
import useInfiniteScroll from '../../Components/ListInfiniteScroll/index.controller';
import ScreenWrapper from '../../Components/ScreenWrapper';
import constants from '../../Config/constants';
import DateHelper from '../../Helper/Date';
import { EventDetails } from '../../RTK/api/events/types';
import { StoreSearchDetails } from '../../RTK/api/store/types';
import { selectSearchFilter } from '../../RTK/filter/selectors';
import themeStyle from '../../Theme/styles';

import ListHeader from './components/Header';
import { TYPE_CARD, TYPE_STATUS, useQuery } from './constants';
import { TYPE } from './types';

const Search = ({ route }) => {
  const typeParams = lodashGet(route, 'params.type');
  const theme = useTheme();
  const homeSearchFilter = useSelector(selectSearchFilter);
  const [search, setSearch] = useState({ value: '', isTag: false });

  const Card = TYPE_CARD[typeParams];

  const payload = useMemo(() => {
    const today = dayjs();
    const currentNextTs = DateHelper.getToNextSlot(today);
    return {
      [TYPE.HOME]: {
        ...(search.isTag ? { tags: search.value } : { name: search.value }),
        ...homeSearchFilter.tags.reduce(
          (a, b) => ({ ...a, [b.paramName]: b.values[0].value }),
          {},
        ),
      },
      [TYPE.RESERVATION]: {
        date: currentNextTs.format(constants.DBDATE_FORMAT),
        time: currentNextTs.format(constants.DBTIME_FORMAT),
        party_size: 1,
        keyword: search.value,
      },
    };
  }, [search.isTag, search.value, homeSearchFilter.tags]);

  const {
    data,
    isLoading,
    isLoadingMore,
    onMomentumScrollBegin,
    onReachedEnd,
  } = useInfiniteScroll<StoreSearchDetails | EventDetails>({
    loaderCount: 2,
    queryParams: payload[typeParams],
    queryOptions: { skip: !search.value },
    useQuery: useQuery[typeParams],
  });

  const showResult = Boolean(search.value) && !isLoading;
  const result = showResult ? data : [];

  return (
    <ScreenWrapper
      header={<PageHeader iconColor={theme['text-basic-color']} />}
      scroll={false}
    >
      <KeyboardAvoidingView
        style={{ flexGrow: 1 }}
        keyboardVerticalOffset={constants.isIOS ? 97 : 0}
        behavior="padding"
        enabled={constants.isIOS}
      >
        <List
          contentContainerStyle={[
            { flexGrow: 1 },
            !lodashIsEmpty(result) && themeStyle.pageBottomSpacer,
          ]}
          data={result}
          ListEmptyComponent={
            showResult && (
              <View
                style={[
                  themeStyle.flex1,
                  themeStyle.flexDirectionRowCenterCenter,
                ]}
              >
                <EmptyData
                  fill={theme[`color-${TYPE_STATUS[typeParams]}-500`]}
                  type="filter"
                />
              </View>
            )
          }
          ListFooterComponent={
            isLoadingMore && (
              <View
                style={[
                  themeStyle.pageVerticalSpacingMedium,
                  themeStyle.alignItemsCenter,
                ]}
              >
                <Spinner status={TYPE_STATUS[typeParams]} />
              </View>
            )
          }
          ListHeaderComponent={
            <ListHeader
              isLoading={isLoading}
              type={typeParams}
              onSearch={(val, isTag) =>
                setSearch({ value: val, isTag: Boolean(isTag) })
              }
            />
          }
          renderItem={({ item }) => (
            <View style={themeStyle.spacingBottom}>
              <Card {...item} />
            </View>
          )}
          onMomentumScrollBegin={onMomentumScrollBegin}
          onEndReached={onReachedEnd}
        />
      </KeyboardAvoidingView>
    </ScreenWrapper>
  );
};

export default Search;
